import {
  Flex,
  Input,
  InputGroup,
  InputRightAddon,
  Link,
  Menu,
  MenuButton,
  MenuDivider,
  MenuGroup,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Text
} from '@chakra-ui/react';
import { SizeType } from '@payflip/types/product-builder';
import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Icon, IconName } from '../../';
import { Button } from '../../../';

type SmartFilterOptions<T> = {
  [property in keyof T]?: {
    title: string;
    options: Array<{
      value: string;
      label: string;
    }>;
  };
};

type SmartFilterProps<T> = {
  initialValue?: SmartFilterSelection<T>;
  selection?: SmartFilterSelection<T>;
  filters?: SmartFilterOptions<T>;
  showActiveSelection?: boolean;
  onChange?: (selection: SmartFilterSelection<T>) => void;
  cms?: {
    clearAll?: string | null;
  };
};

type SmartFilterSelection<T> = {
  [property in keyof T]?: Array<T[property]>;
};

const Ellipsis = styled.span`
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ActiveSelection = styled(Flex)`
  border-radius: 5px;
  border: 1px solid ${({ theme }): string => theme.palette_gray_60};
  align-items: center;
  font-size: 0.9rem;
  white-space: nowrap;
`;

const SelectionTitle = styled(Text)`
  color: ${({ theme }): string => theme.palette_gray_60};
`;

function SmartFilter<T>({
  initialValue,
  filters,
  showActiveSelection = true,
  onChange,
  cms,
  selection
}: SmartFilterProps<T>): JSX.Element {
  const [value, setValue] = useState<SmartFilterSelection<T>>(
    initialValue ??
      Object.keys(filters ?? {}).reduce((acc, v) => ({ ...acc, [v]: [] }), {})
  );
  const [query, setQuery] = useState<{
    [property in keyof T]?: string | undefined;
  }>({});

  function reset(property: keyof T): void {
    setValue((prev) => ({
      ...prev,
      [property]: []
    }));
  }

  function clearAll(): void {
    setValue(
      Object.keys(filters ?? {}).reduce((acc, v) => ({ ...acc, [v]: [] }), {})
    );
  }

  const onChangeRef = useRef(onChange);
  const changeFn = useCallback(
    () => onChangeRef.current?.(value),
    [onChangeRef, value]
  );

  useEffect(() => {
    changeFn();
  }, [changeFn]);

  const actualValue = selection ?? value;
  return (
    <Flex direction="column" gap={5}>
      <Flex direction="row" gap={5}>
        <SelectionTitle>Filter:</SelectionTitle>
        {Object.keys(filters ?? {})?.map((p) => {
          const property = p as keyof T;
          return (
            <Menu closeOnSelect={false} key={p}>
              <MenuButton>
                {filters?.[property]?.title}
                &nbsp;
                {!!actualValue[property]?.length && (
                  <span>({actualValue[property]?.length})</span>
                )}
                <Icon height={'.5rem'} name={IconName.ArrowDown} />
              </MenuButton>
              <MenuList overflowY={'scroll'} maxHeight={'400px'}>
                <MenuGroup>
                  <Flex gap={3} direction="column">
                    {!!actualValue[property]?.length && (
                      <Flex
                        justifyContent="space-between"
                        alignItems="center"
                        px={4}
                      >
                        <span>
                          {actualValue[property]?.length ?? 0} selected
                        </span>
                        <Link onClick={(): void => reset(property)}>Reset</Link>
                      </Flex>
                    )}
                    <Flex
                      justifyContent="space-between"
                      alignItems="center"
                      px={4}
                    >
                      <InputGroup>
                        <Input
                          placeholder={`Search for ${filters?.[property]?.title}`}
                          onChange={(e): void =>
                            setQuery({ ...query, [property]: e.target.value })
                          }
                        />
                        {!!query[property]?.length && (
                          <InputRightAddon
                            onClick={(): void =>
                              setQuery({ ...query, [property]: undefined })
                            }
                            cursor="pointer"
                          >
                            <Icon name={IconName.Close} />
                          </InputRightAddon>
                        )}
                      </InputGroup>
                    </Flex>
                  </Flex>
                </MenuGroup>
                <MenuDivider />
                <MenuGroup>
                  <MenuOptionGroup
                    type="checkbox"
                    value={actualValue[property] as string[]}
                    onChange={(changes): void =>
                      setValue({
                        ...actualValue,
                        [property]:
                          typeof changes === 'string' ? [changes] : changes
                      })
                    }
                  >
                    {filters?.[property]?.options
                      .filter((option) =>
                        query[property]
                          ? new RegExp(query[property] as string, 'i').test(
                              option.label
                            )
                          : true
                      )
                      .map((option) => (
                        <MenuItemOption key={option.value} value={option.value}>
                          <Text casing="capitalize">{option.label}</Text>
                        </MenuItemOption>
                      ))}
                  </MenuOptionGroup>
                </MenuGroup>
              </MenuList>
            </Menu>
          );
        })}
      </Flex>
      {showActiveSelection && Object.keys(actualValue)?.length > 0 && (
        <Flex gap={5} flexWrap="wrap">
          {Object.keys(actualValue).map((p) => {
            const property = p as keyof T;
            return (
              !!actualValue[property]?.length && (
                <ActiveSelection pl={3} key={p}>
                  <SelectionTitle>
                    {filters?.[property]?.title}:&nbsp;
                  </SelectionTitle>
                  <Ellipsis>
                    {actualValue[property]
                      ?.map(
                        (value) =>
                          filters?.[property]?.options.find(
                            (o) => o.value === value
                          )?.label
                      )
                      ?.join(', ')}
                  </Ellipsis>
                  <Button
                    variant="tertiary"
                    size={SizeType.Small}
                    onClick={(): void => reset(property)}
                  >
                    <Icon name={IconName.Close} />
                  </Button>
                </ActiveSelection>
              )
            );
          })}
          {Object.keys(actualValue)
            .map((k) => actualValue[k as keyof T])
            .some((a) => !!a?.length) && (
            <Button
              size="small"
              variant="secondary"
              onClick={(): void => clearAll()}
            >
              <Icon name={IconName.Trash} mr={2} />{' '}
              {cms?.clearAll ?? 'Clear All'}
            </Button>
          )}
        </Flex>
      )}
    </Flex>
  );
}

export { SmartFilter, SmartFilterOptions, SmartFilterSelection };
